import React, { useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  HStack,
  VStack,
  Select,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import FormController from "components/ui/form/FormController";
import { useCreateTeam, useUpdateTeam } from "queries/useTeam";

const TeamModal = ({ isOpen, onClose, team }) => {
  const {
    onSubmit,
    isLoading,
    form: { control, formErrors, formRules, isDirty },
    title,
    actionButtonTitle,
    loadingButtonTitle,
  } = useTeamModal({ onClose, isOpen, team });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={onSubmit}>
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormController
                er
                control={control}
                name="name"
                label="Name"
                rules={{ ...formRules.name }}
                errors={formErrors}
                isDisabled={isLoading}
                render={({ field }) => <Input {...field} />}
              />
              <FormController
                control={control}
                name="city"
                label="City"
                rules={{ ...formRules.city }}
                errors={formErrors}
                isDisabled={isLoading}
                render={({ field }) => <Input {...field} />}
              />
              <FormController
                control={control}
                name="league"
                label="League"
                rules={{ ...formRules.league }}
                errors={formErrors}
                isDisabled={isLoading}
                render={({ field }) => (
                  <Select {...field}>
                    <option value="NFL">NFL</option>
                    <option value="XFL">XFL</option>
                    <option value="CFL">CFL</option>
                    <option value="UFL">UFL</option>
                    <option value="NFLPA">NFLPA</option>
                    <option value="College">College</option>
                  </Select>
                )}
              />
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack>
              <Button onClick={onClose} variant="ghost">
                Close
              </Button>
              <Button
                isDisabled={!isDirty}
                type="submit"
                isLoading={isLoading}
                loadingText={loadingButtonTitle}
              >
                {actionButtonTitle}
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default TeamModal;

const useTeamModal = ({ isOpen, onClose, team = {} }) => {
  const createTeam = useCreateTeam({ onSuccess: () => onClose() });
  const updateTeam = useUpdateTeam({ onSuccess: () => onClose() });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors: formErrors, isDirty },
  } = useForm();

  useEffect(() => {
    if (isOpen) reset(team);
    else reset({});
  }, [isOpen, team]);

  const onSubmit = (data) =>
    team?.id
      ? updateTeam.mutateAsync({ ...team, ...data })
      : createTeam.mutateAsync(data);

  return {
    form: {
      isDirty,
      control,
      formErrors,
      formRules: {
        name: {
          required: "Name is required",
        },
        city: {
          required: "City is required",
        },
      },
    },
    onSubmit: handleSubmit(onSubmit),
    isLoading: team?.id ? updateTeam.isLoading : createTeam.isLoading,
    title: team?.id ? "Edit Team" : "Add Team",
    actionButtonTitle: team?.id ? "Update" : "Create",
    loadingButtonTitle: team?.id ? "Updating..." : "Creating...",
  };
};
